import { LinearIcon } from "@storytell/chat-ui/tools";
import { BUILT_IN_DEFAULT_TAGS, type LiveSummaryTag } from "@storytell/ui/live-summary";
import { TbArrowRight, TbBrandGmail, TbBrandSlack, TbMessage2, TbSend } from "solid-icons/tb";
import { createSignal, For, type JSX, Show } from "solid-js";
import { COMPETITORS } from "~/components/landing/compareData";
import {
  DoneRow,
  HandoffCard,
  NeedsCard,
  NeedsGroup,
  NotesLine,
  NotesPage,
  NotesTopic,
  OfferFoot,
  PaneSection,
  SelectedNotes,
  WorkingRow,
} from "~/components/marketing/mockups";
import "~/components/marketing/mockups/mockups.css";
import { BRAND_TEXT, Eyebrow, SectionTitle } from "~/components/marketing/ui";
import { useI18n } from "~/i18n";

const ExampleFrame = (props: { label: string; children: JSX.Element }) => (
  <div class="overflow-hidden rounded-2xl border border-primary-border bg-canvas shadow-xl shadow-black/5">
    <div class="flex items-center gap-1.5 border-b border-primary-border bg-panel/60 px-3 py-2">
      <span class="size-2 rounded-full bg-red-400/70" />
      <span class="size-2 rounded-full bg-yellow-400/70" />
      <span class="size-2 rounded-full bg-green-400/70" />
      <span class="ml-2 text-[10px] text-secondary-text">{props.label}</span>
    </div>
    {props.children}
  </div>
);

const BriefCards = (props: { label: string; items: { title: string; detail: string }[] }) => (
  <ExampleFrame label={props.label}>
    <div class="flex min-h-44 flex-col gap-3 p-5">
      <For each={props.items}>
        {(item) => (
          <div class="rounded-lg border border-primary-border bg-panel p-3">
            <p class="text-sm font-semibold">{item.title}</p>
            <p class="mt-1 text-xs text-secondary-text">{item.detail}</p>
          </div>
        )}
      </For>
    </div>
  </ExampleFrame>
);

const LAUNCH_TAG = { id: "tag:launch", name: "Launch", color: "#14b8a6" };
const [TODO_TAG, DECISION_TAG] = BUILT_IN_DEFAULT_TAGS;

type Note = { topic: string; text: string; tag?: LiveSummaryTag; owner?: string; sub?: boolean };

const NOTES: Note[] = [
  { topic: "Launch date", text: "Launch moves to the 14th.", tag: DECISION_TAG },
  { topic: "Launch date", text: "QA needs two more days on the checkout flow." },
  { topic: "Launch date", text: "Priya is running the regression pass.", sub: true },
  { topic: "Announcement", text: "Sam drafts the announcement by Thursday.", tag: TODO_TAG, owner: "Sam" },
  { topic: "Announcement", text: "The blog post and the customer email go out the same morning." },
  { topic: "Pricing", text: "The pricing page ships with the launch.", tag: LAUNCH_TAG },
  { topic: "Pricing", text: "Dana asked about annual pricing again." },
];

const NOTE_PILLS = [DECISION_TAG, TODO_TAG, LAUNCH_TAG].map((tag) => ({
  tag,
  count: NOTES.filter((note) => note.tag?.id === tag.id).length,
}));

type Icon = (props: { class?: string }) => JSX.Element;

// The labels are the desktop Activity panel's own: each tool's group, the card's
// named button, and the line a finished follow-up reports where the work landed.
const FOLLOW_UPS: { what: string; why: string; icon: Icon; group: string; verb: string; done: string }[] = [
  {
    what: "Assign the launch ticket to Sam",
    why: "Sam just took the announcement, and the ticket has no owner.",
    icon: LinearIcon,
    group: "Linear issues",
    verb: "Add in Linear",
    done: "Filed in Linear",
  },
  {
    what: "Post the new launch date in #launch",
    why: "The room moved it to the 14th. The channel still says the 7th.",
    icon: TbBrandSlack,
    group: "Slack messages",
    verb: "Send in Slack",
    done: "Done in Slack",
  },
  {
    what: "Draft the pricing reply to Dana",
    why: "Dana asked about annual pricing again.",
    icon: TbBrandGmail,
    group: "Gmail drafts",
    verb: "Save draft in Gmail",
    done: "Draft ready",
  },
];

const LiveCallView = () => (
  <ExampleFrame label="Launch planning · live">
    <div class="mk-app grid bg-panel sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <NotesPage
        project="Hooli launch"
        title="Launch planning"
        meta="Mon, 10:00 AM"
        total={NOTES.length}
        pills={NOTE_PILLS}
        summary="Launch moves to the 14th, and Sam owns the announcement."
      >
        <For each={NOTES}>
          {(note, i) => (
            <>
              <Show when={note.topic !== NOTES[i() - 1]?.topic}>
                <NotesTopic text={note.topic} />
              </Show>
              <NotesLine text={note.text} tag={note.tag} owner={note.owner} sub={note.sub} />
            </>
          )}
        </For>
      </NotesPage>
      <div class="flex flex-col gap-2 border-t border-(--cb-divider) p-2 sm:border-s sm:border-t-0">
        <PaneSection verb="waiting" count={FOLLOW_UPS.length} />
        <For each={FOLLOW_UPS}>
          {(followUp) => (
            <NeedsGroup icon={<followUp.icon class="size-3" />} label={followUp.group} count={1}>
              <NeedsCard
                title={followUp.what}
                foot={<OfferFoot icon={<followUp.icon class="size-3.5 shrink-0" />} label={followUp.verb} />}
              >
                <p class="mt-1 text-xs leading-relaxed text-secondary-text">{followUp.why}</p>
              </NeedsCard>
            </NeedsGroup>
          )}
        </For>
      </div>
    </div>
  </ExampleFrame>
);

const ResultsView = () => (
  <ExampleFrame label="Activity · after the call">
    <div class="mk-app bg-panel p-2">
      <PaneSection verb="did" count={FOLLOW_UPS.length} />
      <For each={FOLLOW_UPS}>{(followUp) => <DoneRow title={followUp.what} where={followUp.done} />}</For>
      <PaneSection verb="waiting" count={1} class="mt-2" />
      <WorkingRow
        title="Check the pricing page against the new date"
        status={
          <>
            <TbMessage2 class="size-3" aria-hidden="true" />
            In a chat
          </>
        }
      />
    </div>
  </ExampleFrame>
);

// Two separate desktop steps: "Turn into…" on selected notes, then the Summary's
// Send to AI agent card that hands the session to an installed agent.
const HandoffView = () => (
  <ExampleFrame label="Launch planning · ended">
    <div class="mk-app flex flex-col gap-4 bg-panel p-4">
      <div>
        <p class="mb-2 text-[11px] font-semibold tracking-wider text-secondary-text uppercase">
          1 · Turn the notes into a PRD
        </p>
        <SelectedNotes
          class="mt-14 mb-8"
          lines={[
            "Launch moves to the 14th.",
            "Sam drafts the announcement by Thursday.",
            "The pricing page ships with the launch.",
          ]}
          formats={["PRD", "Brief", "Plan", "Canvas"]}
          picked={0}
        />
      </div>
      <div>
        <p class="mb-2 text-[11px] font-semibold tracking-wider text-secondary-text uppercase">
          2 · Send the session to an agent
        </p>
        <HandoffCard agent="Claude Code" folder="~/code/launch-site">
          <span class="flex min-h-9 items-center justify-center gap-2 rounded-lg bg-brand text-sm font-medium text-white">
            <TbSend class="size-4" aria-hidden="true" />
            Send to AI agent
          </span>
        </HandoffCard>
      </div>
    </div>
  </ExampleFrame>
);

const STAGES: {
  id: string;
  tab: string;
  time: string;
  title: string;
  chief: string;
  noteTakers?: string;
  visual: () => JSX.Element;
}[] = [
  {
    id: "before",
    tab: "Before",
    time: "Mon 9:55",
    title: "You walk in with last week's context.",
    chief:
      "Set up a meeting-prep Action, and the brief for this call brings in what you decided and promised last time.",
    visual: () => (
      <BriefCards
        label="Brief · Launch planning"
        items={[
          { title: "Follow up", detail: "Dana's pricing question from last week's call." },
          { title: "Last decided", detail: "The pricing page copy is signed off." },
        ]}
      />
    ),
  },
  {
    id: "during",
    tab: "During",
    time: "10:00–10:31",
    title: "You see decisions and to-dos as the call goes.",
    chief:
      "Chief organizes the conversation as it unfolds, names owners when someone states them, and applies your project's Auto-apply Tags. You choose which suggested work Chief takes on while you're still on the call.",
    noteTakers: "A transcript and a summary. Otter adds a shared transcript you can annotate live.",
    visual: () => <LiveCallView />,
  },
  {
    id: "hang-up",
    tab: "Hang-up",
    time: "Mon 10:31",
    title: "You review what Chief finished.",
    chief:
      "The work you gave Chief during the call lands under Done in Activity, with a link to each result. Slack and Linear changes ask for your approval unless you've pre-approved them.",
    noteTakers: "The recap is ready.",
    visual: () => <ResultsView />,
  },
  {
    id: "next-day",
    tab: "Next day",
    time: "Tue 11:00",
    title: "Sam turns the discussion into a working draft.",
    chief:
      "Sam selects the notes that matter and turns them into a PRD. Then Sam sends the session to Claude Code, with the context it needs to keep working.",
    noteTakers: "You search the recap or ask it questions.",
    visual: () => <HandoffView />,
  },
  {
    id: "next-call",
    tab: "Next call",
    time: "Mon 9:55",
    title: "You bring last week's context into the next call.",
    chief: "Your meeting-prep Action puts earlier decisions and commitments into the next brief.",
    noteTakers: "You search last week's recap.",
    visual: () => (
      <BriefCards
        label="Brief · Launch planning, week 2"
        items={[
          { title: "Launch", detail: "The 14th. Sam owns the announcement." },
          { title: "Follow up", detail: "Dana's pricing question." },
        ]}
      />
    ),
  },
];

// Stages sit at even intervals, each centred over its tab.
const stageLeft = (i: number) => 10 + i * 20;

export const CompetitorMarks = (props: { size?: "sm" }) => (
  <div class="flex shrink-0 -space-x-1.5">
    <For each={COMPETITORS}>
      {(c) => (
        <div
          class={`flex items-center justify-center overflow-hidden rounded-full ring-2 ring-tile ${c.bg} ${
            props.size === "sm" ? "size-5" : "size-9"
          }`}
        >
          <img
            src={c.src}
            alt={c.name}
            class={
              c.bg
                ? `object-contain brightness-0 invert ${props.size === "sm" ? "size-3" : "size-5"}`
                : "size-full object-cover"
            }
          />
        </div>
      )}
    </For>
  </div>
);

const TAB_STEPS: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };

export const CompareTimeline = () => {
  const { t } = useI18n();
  const [selected, setSelected] = createSignal(1);
  const stage = () => STAGES[selected()];

  const onTabKeyDown = (event: KeyboardEvent & { currentTarget: HTMLDivElement }) => {
    const step = TAB_STEPS[event.key];
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? STAGES.length - 1
          : step === undefined
            ? undefined
            : (selected() + step + STAGES.length) % STAGES.length;
    if (next === undefined) return;
    event.preventDefault();
    setSelected(next);
    event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };

  return (
    <section aria-labelledby="compare-week" class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <Eyebrow label={t("marketing.compare.timeline.eyebrow")} />
        <SectionTitle id="compare-week" text={t("marketing.compare.timeline.title")} class="mt-5" />
        <p class="mt-5 mb-12 max-w-3xl text-lg leading-relaxed text-secondary-text">
          A Monday call about moving the launch. Click a moment to see what you have in Chief.
        </p>

        <div class="rounded-2xl border border-primary-border bg-tile p-4 sm:p-8">
          <div class="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-x-3 sm:grid-cols-[104px_minmax(0,1fr)]">
            <div class="flex flex-col gap-1.5">
              <CompetitorMarks size="sm" />
              <span class="text-xs text-secondary-text">Note-takers</span>
            </div>
            <div class="relative h-12" aria-hidden="true">
              <div
                class="absolute inset-y-0 rounded-md bg-secondary-text/5"
                style={{ left: `${stageLeft(1)}%`, width: `${stageLeft(2) - stageLeft(1)}%` }}
              />
              <span
                class="absolute top-0.5 text-center text-[10px] tracking-wider text-secondary-text uppercase"
                style={{ left: `${stageLeft(1)}%`, width: `${stageLeft(2) - stageLeft(1)}%` }}
              >
                On the call
              </span>
              <div
                class="absolute top-2/3 h-0.5 -translate-y-1/2 bg-secondary-text/40"
                style={{ left: `${stageLeft(1)}%`, width: `${stageLeft(2) - stageLeft(1)}%` }}
              />
              <div
                class="absolute top-2/3 border-t border-dashed border-secondary-text/25"
                style={{ left: `${stageLeft(2)}%`, right: `${100 - stageLeft(4)}%` }}
              />
              <span
                class="absolute top-2/3 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary-text"
                style={{ left: `${stageLeft(2)}%` }}
              />
            </div>

            <div class="flex flex-col gap-1.5 pt-3">
              <img src="/images/chief-logo-gold-crown-only.png" alt="" class="h-4 w-auto self-start" />
              <span class="text-xs text-primary-text">Chief</span>
            </div>
            <div class="relative mt-3 h-10" aria-hidden="true">
              <div
                class="absolute top-1/2 h-0.5 -translate-y-1/2 bg-brand/70"
                style={{ left: `${stageLeft(0)}%`, right: `${100 - stageLeft(4)}%` }}
              />
              <For each={STAGES}>
                {(_, i) => (
                  <span
                    class={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-4 ring-tile transition-all ${
                      selected() === i() ? "size-3.5 bg-accent" : "size-2 bg-brand"
                    }`}
                    style={{ left: `${stageLeft(i())}%` }}
                  />
                )}
              </For>
            </div>

            <div />
            <div
              role="tablist"
              aria-label="Moments in the week"
              class="grid grid-cols-5 gap-1.5"
              onKeyDown={onTabKeyDown}
            >
              <For each={STAGES}>
                {(item, i) => (
                  <button
                    type="button"
                    role="tab"
                    id={`compare-timeline-tab-${item.id}`}
                    aria-selected={selected() === i()}
                    aria-controls="compare-timeline-panel"
                    tabIndex={selected() === i() ? 0 : -1}
                    onClick={() => setSelected(i())}
                    class={`cursor-pointer rounded-lg border px-1 py-2 text-center transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none ${
                      selected() === i()
                        ? "border-brand-border bg-brand/10 text-primary-text"
                        : "border-primary-border text-secondary-text hover:bg-secondary-text/5 hover:text-primary-text"
                    }`}
                  >
                    <span class="block text-[10px] tabular-nums sm:text-[11px]">{item.time}</span>
                    <span class="block text-xs font-semibold sm:text-sm">{item.tab}</span>
                  </button>
                )}
              </For>
            </div>
          </div>

          <div
            role="tabpanel"
            id="compare-timeline-panel"
            aria-labelledby={`compare-timeline-tab-${stage().id}`}
            class="mt-8 grid items-start gap-8 border-t border-primary-border pt-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.5fr)] lg:gap-12"
          >
            <div>
              <Eyebrow label={stage().time} class={`mb-3 ${BRAND_TEXT}`} />
              <h3 class="mb-4 text-2xl font-semibold tracking-tight text-balance sm:text-3xl">{stage().title}</h3>
              <p class="text-lg leading-relaxed text-secondary-text">{stage().chief}</p>
              <Show when={stage().noteTakers}>
                {(line) => (
                  <div class="mt-6 flex items-start gap-3 text-sm text-secondary-text">
                    <CompetitorMarks size="sm" />
                    <span>
                      <span class="font-semibold text-primary-text">Note-takers: </span>
                      {line()}
                    </span>
                  </div>
                )}
              </Show>
              <Show when={STAGES[selected() + 1]}>
                {(next) => (
                  <button
                    type="button"
                    onClick={() => {
                      const nextIndex = selected() + 1;
                      setSelected(nextIndex);
                      // On the last moment this button unmounts; hand focus to that moment's tab.
                      if (!STAGES[nextIndex + 1])
                        document.getElementById(`compare-timeline-tab-${STAGES[nextIndex].id}`)?.focus();
                    }}
                    class="mt-8 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-primary-border bg-panel px-5 py-3 text-sm font-semibold text-primary-text transition-colors hover:border-brand focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
                  >
                    Then: {next().tab}
                    <TbArrowRight class="size-4" aria-hidden="true" />
                  </button>
                )}
              </Show>
            </div>
            {stage().visual()}
          </div>
        </div>
      </div>
    </section>
  );
};
